import Vue from 'vue'
import Vuex from 'vuex'
import axios from './axios-auth'
import router from '../router/index.js'
import createPersistedState from "vuex-persistedstate";
import { trackingMutations, trackingActions, trackingGetters } from '@/utils/tracking';
import { getProductById } from '@/utils/productData';

Vue.use(Vuex)

function toNumericPrice(value) {
  if (value === null || value === undefined || value === '') return NaN;
  const n = parseFloat(String(value).replace('$', ''));
  return isNaN(n) ? NaN : n;
}

function lookupCachedPricing(id) {
  if (!id) return null;
  try {
    const matches = getProductById(id);
    const product = Array.isArray(matches) ? matches[0] : matches;
    return product && product.pricing ? product.pricing : null;
  } catch (_) {
    return null;
  }
}

function resolveItemPrice(item) {
  const direct = toNumericPrice(item && item.price);
  if (!isNaN(direct)) return direct;
  const pricing = lookupCachedPricing(item && item.id);
  if (pricing) {
    const candidate = toNumericPrice(pricing.discounted_price);
    if (!isNaN(candidate)) return candidate;
    const retail = toNumericPrice(pricing.retail_price);
    if (!isNaN(retail)) return retail;
    const plain = toNumericPrice(pricing.price);
    if (!isNaN(plain)) return plain;
  }
  return 0;
}

function resolveItemRetailPrice(item) {
  const direct = toNumericPrice(item && item.retail_price);
  if (!isNaN(direct)) return direct;
  const pricing = lookupCachedPricing(item && item.id);
  if (pricing) {
    const retail = toNumericPrice(pricing.retail_price);
    if (!isNaN(retail)) return retail;
    const plain = toNumericPrice(pricing.price);
    if (!isNaN(plain)) return plain;
  }
  return resolveItemPrice(item);
}

export default new Vuex.Store({
  state: {
    // Existing state
    accessToken: null,
    userData: [],
    progressItems: [],
    lastCheckedProgress: null,
    loggingIn: false,
    transactionItems: [],
    latestPrescription:[],
    appointments: null,
    lab: null,
    priscription: null,
    shipping: null,
    tracking: null,
    ipAddress: null,
    medForm: null,
    prescriptionPreauths: null,
    lastCheckedSubscription: null,
    activeHomeTab: 'myNewSelf',
    subscriptionItems:[],
    lastCheckedTransactions: null,
    trackingData: [], 
    // Cart state
    cartItems: [],
    isCartOpen: false,
    weightLossOffer: null,
    // New product-related state
    currentCategory: 'WEIGHT LOSS',
    loading: false,
    error: null,
    offerSema: 'sema_plan_278928',
    offerTir: 'tirz_plan_1',
    chatProfile:null,
    case: {id: "" , provider: ""},
    doctorProfile: null,
    encounter: null,
    docChat: false,
    // Dashboard theme
    theme: 'light'
  },
  
  mutations: {
    ...trackingMutations,
    SET_SEMA_OFFER(state, offer) {
      const offerSemaMap = {
        A: 'sema_plan_134562',
        C: 'sema_plan_367367',
      };
    
      // Assign based on offer, defaulting to 'sema_plan_278928'
      state.offerSema = offerSemaMap[offer] || 'sema_plan_278928';
    },

    SET_ENCOUNTER_ID(state, order) {
      state.encounter = order
    },

    SET_TIR_OFFER(state, offer) {
      const offerTirMap = {
        C: 'tirz_plan_3',
        B: 'tirz_plan_2',
      };
      // Assign based on offer, defaulting to 'tirz_plan_1'
      state.offerTir = offerTirMap[offer] || 'tirz_plan_1';
    },

    SET_Transactions_ITEMS(state, payload) {
      
      if (payload && payload.items) {
        state.transactionItems = payload.items;
        state.lastCheckedTransactions = payload.timestamp;
      }
    },
    SET_SUBSCRIPTION_ITEMS(state, payload) {
      
      if (payload && payload.items) {
        state.subscriptionItems = payload.items;
        state.lastCheckedSubscription = payload.timestamp;
      }
    },
    SET_PROGRESS_ITEMS(state, payload) {
      
      if (payload && payload.items) {
        state.progressItems = payload.items;
        state.lastCheckedProgress = payload.timestamp;
      }
    },
    SET_HEALTH_ID(state, payload) {
      if (payload && payload.health_id &&(!state.userData.health_id || state.userData.health_id === '')){
        state.userData.health_id = payload.health_id;
      }
    },
    SET_PRESCRIPTION_PREAUTHS(state, preauths) {
      state.priscription = preauths;
    },
    REMOVE_PRESCRIPTION(state, orderId) {
      state.priscription = state.priscription.filter(
        preauth => preauth.orderId !== orderId
      );
    },
    // Existing mutations remain the same
    loginStart: state => state.loggingIn = true,
    updateAccessToken: (state, accessToken) => {
      state.accessToken = accessToken;
    },
    updateUserData: (state, userData) => {
      state.userData = { ...state.userData, ...userData }
    },
    logout: (state) => {
      state.accessToken = null;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
    },
    
    SaveShippingDetails: (state, shipping) => {
      state.shipping = shipping
    },
    setIpAddress(state, ipAddress) {
      state.ipAddress = ipAddress;
    },

    // Cart mutations
    ADD_TO_CART(state, item) {
      console.log('Cart item being added to cart in store:', item); // Debug log
      const existingItem = state.cartItems.find(i => i.id === item.id);

      const pricing = lookupCachedPricing(item && item.id) || {};
      const pickFirstNumeric = (...candidates) => {
        for (const c of candidates) {
          const n = toNumericPrice(c);
          if (!isNaN(n)) return n;
        }
        return undefined;
      };
      let resolvedPrice = pickFirstNumeric(item.price, pricing.discounted_price, pricing.retail_price, pricing.price);
      let resolvedRetail = pickFirstNumeric(item.retail_price, pricing.retail_price, pricing.price, resolvedPrice);

      if (resolvedPrice === undefined) {
        console.warn('ADD_TO_CART: no price resolvable for item, defaulting to 0', item && item.id);
        resolvedPrice = 0;
      }
      if (resolvedRetail === undefined) {
        resolvedRetail = resolvedPrice;
      }

      // Create the new item with all properties explicitly
      const newItem = {
          id: item.id,
          name: item.name,
          price: resolvedPrice,
          retail_price: resolvedRetail,
          quantity: item.quantity,
          main_type: item.main_type,
          subheader: item.subheader,
          image: item.image,
          category: item.category,
          sku: item.sku,
          stripe_id: item.stripe_id,
          is_addon: item.is_addon
      };
      console.log(newItem)
      if (existingItem) {
        existingItem.quantity += 1;
      } else {
          state.cartItems.push(newItem);
      }
      /*if (item.main_type === "prescription") {
          if (!existingItem) {
              state.cartItems.push(newItem);
          }
      } else {
          if (existingItem) {
              existingItem.quantity += 1;
          } else {
              state.cartItems.push(newItem);
          }
      }*/
  },
    
    REMOVE_FROM_CART(state, itemId) {
      state.cartItems = state.cartItems.filter(item => item.id !== itemId);
    },
    
    UPDATE_CART_QUANTITY(state, { itemId, quantity }) {
      const item = state.cartItems.find(i => i.id === itemId);
      if (item) {
        if (item.main_type === "Prescription") {
          item.quantity = 1;
        } else {
          if (quantity > 0) {
            item.quantity = quantity;
          } else {
            state.cartItems = state.cartItems.filter(i => i.id !== itemId);
          }
        }
      }
    },
    
    CLEAR_CART(state) {
      state.cartItems = [];
    },
    
    SET_CART_VISIBILITY(state, isVisible) {
      state.isCartOpen = isVisible;
    },

    // New product-related mutations
    SET_CURRENT_CATEGORY(state, category) {
      state.currentCategory = category;
    },
    
    SET_LOADING(state, status) {
      state.loading = status;
    },
    SET_ACTIVE_HOME_TAB(state, tab) {
        state.activeHomeTab = tab;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_CHAT_PROFILE(state, profile) {
      state.chatProfile = profile;
    },
    SET_NEW_PHONE(state, phone) {
      state.userData.phone = phone;
    },
    SET_NEW_EMAIL(state, email) {
      state.userData.email = email;
    },
    SET_PRESCRIPTION(state, prescription) {
      state.latestPrescription = prescription;
    },
    SET_CASE_ID(state, order) {
      state.case = order
    },
    SET_DOC_CHAT(state) {
      state.docChat = !state.docChat;
    },
    SET_DOC_PROFILE(state, doctorProfile) {
      state.doctorProfile = doctorProfile
    },
    // Dashboard theme
    SET_THEME(state, theme) {
      state.theme = theme
      document.documentElement.setAttribute('data-theme', theme)
    },

  },

  actions: {
    ...trackingActions,
      setChatProfile({ commit }, profileData) {
        try {
            // Validate required fields
            if (!profileData || !profileData.email || !profileData.first_name) {
                throw new Error('Invalid chat profile data');
            }

            // Store in localStorage for persistence
            localStorage.setItem('chatProfile', JSON.stringify(profileData));
            commit('SET_CHAT_PROFILE', profileData);
            
            return Promise.resolve(profileData);
        } catch (error) {
            console.error('Error setting chat profile:', error);
            return Promise.reject(error);
        }
    },
    updateUserPhone({ commit }, phone) {
      commit('SET_NEW_PHONE', phone);
    },
    updateUserEmail({ commit }, email) {
      commit('SET_NEW_EMAIL', email);
    },
    
    addSemaOffer({ commit }, offer) {
      commit('SET_SEMA_OFFER', offer);
    },
    addTirOffer({ commit }, offer) {
      console.log("setting offer")
      commit('SET_TIR_OFFER', offer);
    },
    updateTransactionItems({ commit }, payload) {
      commit('SET_Transactions_ITEMS', payload);
    },
    updateSubscriptions({ commit }, payload) {
      commit('SET_SUBSCRIPTION_ITEMS', payload);
    },
    updateProgress({ commit }, payload) {
      commit('SET_PROGRESS_ITEMS', payload);
    },
    updateHealth({ commit }, payload) {
      commit('SET_HEALTH_ID', payload);
    },
    setPrescriptionPreauths({ commit }, preauths) {
      commit('SET_PRESCRIPTION_PREAUTHS', preauths);
    },
    UpdateUserData({ commit }, userData) {
      commit('updateUserData', userData)
      return Promise.resolve() // Return a promise for proper async handling
    },
    removePrescription({ commit }, orderId) {
      commit('REMOVE_PRESCRIPTION', orderId);
    },
    // Existing actions remain the same
    saveTransaction({ commit }, data) {
      commit('SaveTransactions', data);
    },
    saveShippingDetails({ commit }, data) {
      commit('SaveShippingDetails', data);
    },
    doLogin({ commit }, loginData) {
      commit('updateAccessToken', loginData.token.token);
      commit('updateUserData', loginData.user);

      try {
        if (window.GuestChatWidget && typeof window.GuestChatWidget.identify === 'function') {
          setTimeout(() => {
            window.GuestChatWidget.identify({
              jwt: loginData.token.token,
              userId: loginData.user.id,
              userName: `${loginData.user.first_name} ${loginData.user.last_name}`,
              userEmail: loginData.user.email
            }).catch(err => console.error('Widget identify error:', err));
          }, 100);
        }
      } catch (error) {
        console.log('Error setting guest login on widget:', error);
      }
    },
    
    logout({ commit }, data) {
      return new Promise((resolve, reject) => {
        let token = data.token;
        axios.get("/api/logout", {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          }
        }).then(async () => {
          commit('updateAccessToken', null);

          if (window.GuestChatWidget && typeof window.GuestChatWidget.logout === 'function') {
            try {
              window.GuestChatWidget.logout();
              await new Promise(resolve => setTimeout(resolve, 200));
            } catch (err) {
              console.error('Error logging out from widget:', err);
            }
          }

          localStorage.removeItem('_ymain_key_');
          router.push({ name: 'LoginPage' });
        }).catch(error => {
          reject(error)
        });
      });
    },

    setActiveHomeTab({ commit }, tab) {
        commit('SET_ACTIVE_HOME_TAB', tab);
    },

    async getIpAddress({ commit }) {
      try {
        const response = await axios.get('https://cors-anywhere.herokuapp.com/http://api.ipify.org/?format=text');
        commit('setIpAddress', response.data);
      } catch (error) {
        console.error('Error fetching IP address:', error);
      }
    },

    // Cart actions
    addToCart({ commit }, item) {
      
      commit('ADD_TO_CART', item);
      commit('SET_CART_VISIBILITY', true);
    },
    
    removeFromCart({ commit }, itemId) {
      commit('REMOVE_FROM_CART', itemId);
    },
    
    updateCartQuantity({ commit }, { itemId, quantity }) {
      commit('UPDATE_CART_QUANTITY', { itemId, quantity });
    },
    
    clearCart({ commit }) {
      commit('CLEAR_CART');
    },
    
    setCartVisibility({ commit }, isVisible) {
      commit('SET_CART_VISIBILITY', isVisible);
    },
    toggeleDocChat({ commit }) {
      commit('SET_DOC_CHAT');
    },
    // Dashboard theme actions
    toggleTheme({ commit, state }) {
      const newTheme = state.theme === 'light' ? 'dark' : 'light'
      commit('SET_THEME', newTheme)
      localStorage.setItem('_ymain_theme_', newTheme)

      if (window.GuestChatWidget && typeof window.GuestChatWidget.setTheme === 'function') {
        try {
          window.GuestChatWidget.setTheme(newTheme);
        } catch (err) {
          console.error('Error setting widget theme:', err);
        }
      }
    },
    // Applies the saved theme and nothing else. Everything outside the dashboard
    // is a light-only design, so a page that reads prefers-color-scheme for
    // itself turns checkout and the post-purchase screens dark for customers who
    // never asked for it. Only initDashboardTheme is allowed to read the OS.
    initTheme({ commit }) {
      // Login stays light: LoginPage clears data-theme, and App.vue's delayed
      // call here used to put dark back. With the dashboard's CSS still loaded
      // (an expired session sent from /home), dark turned webflow's black text
      // near-white on the white login page.
      if (window.location.pathname === '/login') return

      const savedTheme = localStorage.getItem('_ymain_theme_')
      const theme = savedTheme === 'dark' ? 'dark' : 'light'

      commit('SET_THEME', theme)

      if (window.GuestChatWidget && typeof window.GuestChatWidget.setTheme === 'function') {
        try {
          const currentPath = window.location.pathname
          const isDashboard = currentPath === '/home' || currentPath.startsWith('/home/')
          if (isDashboard) {
            window.GuestChatWidget.setTheme(theme)
          }
        } catch (err) {
          console.error('Error setting widget theme:', err)
        }
      }
    },
    // The dashboard is the one surface that may adopt the OS setting, and it does
    // it once: the first visit writes the result to _ymain_theme_ so from then on
    // it is a stored choice like any other. Every page outside the dashboard then
    // reads that stored choice through initTheme and never guesses for itself.
    initDashboardTheme({ dispatch }) {
      if (localStorage.getItem('_ymain_theme_')) {
        return dispatch('initTheme')
      }

      const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
      localStorage.setItem('_ymain_theme_', prefersDark ? 'dark' : 'light')

      return dispatch('initTheme')
    },
    syncWidgetTheme({ state }) {
      const currentPath = window.location.pathname
      const isDashboard = currentPath === '/home' || currentPath.startsWith('/home/')

      if (window.GuestChatWidget && typeof window.GuestChatWidget.setTheme === 'function') {
        try {
          if (isDashboard) {
            window.GuestChatWidget.setTheme(state.theme)
          } else {
            window.GuestChatWidget.setTheme('light')
          }
        } catch (err) {
          console.error('Error syncing widget theme:', err)
        }
      }
    },
    // New product-related actions
    setCategory({ commit }, category) {
      commit('SET_CURRENT_CATEGORY', category);
    },
    
    setLoading({ commit }, status) {
      commit('SET_LOADING', status);
    },
    
    setError({ commit }, error) {
      commit('SET_ERROR', error);
    },
    setPrescription({ commit }, prescription) {
      commit('SET_PRESCRIPTION', prescription);
    },
    setCaseID({ commit }, order) {
      commit('SET_CASE_ID', order);
    },
    
    setEncounterId({ commit }, order) {
      commit('SET_ENCOUNTER_ID', order);
    },

    setDocProfile({ commit }, docProfile) {
      commit('SET_DOC_PROFILE', docProfile);
    },
  },

  getters: {
    ...trackingGetters,
    offerSema: state => state.offerSema,
    docChatStatus: state => state.docChat,
    offerTir: state => state.offerTir,
    lastCheckedProgress: state => state.lastCheckedProgress,
    progressItems: state => state.progressItems,
    user: state => state.userData,
    isAuthenticated: state => state.accessToken !== null,
    isAdmin: state => state.userData?.is_admin === 1,
    authToken: state => state.accessToken,
    transactionItems: state => state.transactionItems,
    lastCheckedTransactions: state => state.lastCheckedTransactions,
    subscription: state => state.subscription,
    appointment: state => state.appointments,
    prescription: state => state.priscription,
    tracking: state => state.tracking,
    lab: state => state.lab,
    shipping: state => state.shipping,
    medForm: state => state.medForm,
    ipAddress: state => state.ipAddress,
    chatProfile: state => state.chatProfile,
    hasChatProfile: state => state.chatProfile!=null,
    activeHomeTab: state => state.activeHomeTab,
    getLatestPrescription: state => {
      // If progressItems is empty or not an array, return empty array
      if (!state.progressItems || !Array.isArray(state.progressItems) || state.progressItems.length === 0) {
        return [];
      }

      // Collect all prescriptions across all orders
      const allPrescriptions = state.progressItems
        .filter(order => order.prescription_data && Array.isArray(order.prescription_data))
        .flatMap(order => order.prescription_data);
      
      // If we found no prescriptions, return empty array
      if (allPrescriptions.length === 0) {
        return [];
      }
      
      return allPrescriptions;
    },
    // Cart getters
    cartItems: state => state.cartItems,
    cartItemCount: state => state.cartItems.reduce((total, item) => total + item.quantity, 0),
    cartTotal: state => {
      return state.cartItems.reduce((total, item) => {
        const price = resolveItemPrice(item);
        const qty = Number(item && item.quantity) || 0;
        return total + (price * qty);
      }, 0);
    },
    cartTotalNoDiscount: state => {
      return state.cartItems.reduce((total, item) => {
        const price = resolveItemRetailPrice(item);
        const qty = Number(item && item.quantity) || 0;
        return total + (price * qty);
      }, 0);
    },

    cartDiscount: state => {
      return state.cartItems.reduce((total, item) => {
        const retailPrice = resolveItemRetailPrice(item);
        const currentPrice = resolveItemPrice(item);
        const qty = Number(item && item.quantity) || 0;
        return total + ((retailPrice - currentPrice) * qty);
      }, 0);
    },
    isCartOpen: state => state.isCartOpen,
    getRemainingPrescriptions: state => state.priscription,
    // New product-related getters
    currentCategory: state => state.currentCategory,
    loading: state => state.loading,
    error: state => state.error,
    lastCheckedSubscription: state => state.lastCheckedSubscription,
    subscriptionItems: state => state.subscriptionItems,
    getCase: state => {
      // If progressItems is empty or not an array, return null
      if (!state.progressItems || !Array.isArray(state.progressItems) || state.progressItems.length === 0) {
        return null;
      }
      console.log(state.progressItems)
      // Filter to orders that have a case with provider === 1
      const ordersWithValidCase = state.progressItems.filter(order => 
        order.external_visit_id && order.provider === 1
      );
      
      if (ordersWithValidCase.length === 0) {
        return null;
      }
      console.log(ordersWithValidCase)
      // If there's only one valid case, return it
      // If there are multiple, return the first one (most recent)
      return {id: ordersWithValidCase[0].external_visit_id,  provider: ordersWithValidCase[0].provider};
    },
    docProfile: state => state.doctorProfile,
    // Dashboard theme getter
    isDarkMode: state => state.theme === 'dark',

    getLatestEncounter : state => state.encounter,
    // Add this new getter to get all orders with prescriptions
    getOrdersWithPrescriptions: state => {
      if (!state.progressItems || !Array.isArray(state.progressItems)) {
        return [];
      }
      
      // An order counts as having a prescription if it has a snapshot (the exact
      // meds sent for that order) or, for older orders with no snapshot, the
      // legacy prescription_data list — mirrors resolvePrescriptions in the
      // dosage components.
      const hasItems = value => Array.isArray(value) && value.length > 0;

      return state.progressItems.filter(order =>
        order && (hasItems(order.prescription_snapshot_data) || hasItems(order.prescription_data))
      );
    }
  },

  plugins: [
    createPersistedState({
      key: '_ymain_key_',
      paths: [
        'accessToken', 
        'userData', 
        'transaction', 
        'subscription', 
        'appointments', 
        'lab', 
        'priscription', 
        'shipping', 
        'tracking',
        'ipAddress',
        'medForm',
        'cartItems',
        'isCartOpen',
        'currentCategory',
        'progressItems', 
        'lastCheckedProgress',
        'subscriptionItems',
        'lastCheckedSubscription',
        'transactionItems',
        'lastCheckedTransactions',
        'offerTir',
        'offerSema',
        'trackingData',
        'chatProfile',
        'latestPrescription',
        'case',
        'doctorProfile',
        'encounter',
        'activeHomeTab'
      ]
    })
  ],
})
